// The core shell (mockup 3, then the rooms of 30-day rows 30 and 31): top bar with the bell, a five-slot tab bar on
// the phone, a sidebar on the desktop. Nav comes from module manifests; the room you are in (Jobs or Bids and the one
// picked, or a room a module brings, or the company's) decides which of it is offered. Every room has a front door.
// The shell holds no module ids: a company without a module never sees its room or its entries.
import { NavLink, useLocation } from 'react-router';
import { useEffect, type ReactNode } from 'react';
import { navFor } from '@tradeview/core';
import { useAuth } from '../auth';
import { useUpdateAvailable, applyUpdate } from '../update';
import { Icon } from './Icon';
import { HelpButton } from './Help';
import { Tour } from './Tour';
import { useAlerts, unreadCount } from './alerts';
import { useRoom, doorOf } from './room';
import { RoomBand } from './RoomBand';

const MORE = { id: 'more', label: 'More', icon: 'more', route: '/modules', order: 999 };
// Inside a job the desk reads the way the day runs: the field's things in the order of a day on site, then the
// office's in the order of a morning at the desk. A module missing from a list falls to the end by its own order.
export const FIELD_ORDER = ['clock', 'records', 'reports', 'channel', 'drawings', 'materials', 'punch', 'schedule'];
export const OFFICE_ORDER = ['plan', 'mail', 'controls', 'procurement', 'billing', 'costing', 'bids', 'service', 'warehouse', 'sops', 'analytics'];
type NavItem = { id: string; label: string; icon: string; route: string; order: number; bundle?: string; room?: string; bidLabel?: string; scope?: 'one' | 'room' };
function dayOrder(items: NavItem[]) {
  const rank = (list: string[], n: NavItem) => { const i = list.indexOf(n.id); return i === -1 ? 100 + n.order : i; };
  const field = items.filter(n => !n.bundle || n.bundle === 'core' || n.bundle === 'field').sort((a, b) => rank(FIELD_ORDER, a) - rank(FIELD_ORDER, b));
  const office = items.filter(n => n.bundle && n.bundle !== 'core' && n.bundle !== 'field').sort((a, b) => rank(OFFICE_ORDER, a) - rank(OFFICE_ORDER, b));
  return [...field, ...office];
}

export function Shell({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const role = session?.role ?? null;
  const ent = session?.entitlements ?? null;
  const phoneNav: NavItem[] = navFor('phone', role, ent);
  const desktopNav: NavItem[] = navFor('desktop', role, ent);
  const { pathname } = useLocation();
  const update = useUpdateAvailable();
  const unread = unreadCount(useAlerts());
  const { rooms, room, project, projects, enterRoom, enterProject } = useRoom();

  // The room's menu: its front door, then every entry whose manifest puts it in this room ('both' = job and bid),
  // in the order the day runs; an entry whose route is the door is the door. ':id' is the picked project.
  const inRoom = (n: NavItem) => !!room && (n.room === room.id || (n.room === 'both' && !!room.picks));
  const withId = (route: string) => route.replace(':id', project?.id ?? '');
  // A room of projects has two levels: the room's own pages (its schedule: every bid, every job) and the one picked
  // (its front door and its pages). The overall view is the room's, never inside an individual bid or job (jb).
  // A single room (Service, Warehouse, Company) has one level: its door, then its pages.
  const picked = !room?.picks || !!project;
  const ordered = (items: NavItem[]): NavItem[] => !room ? [] : (room.picks === 'job' ? dayOrder(items.filter(inRoom)) : items.filter(inRoom).sort((a, b) => a.order - b.order)).filter(n => n.route !== room.door).map(n => ({ ...n, label: room.picks === 'bid' && n.bidLabel ? n.bidLabel : n.label }));
  const door = (): NavItem[] => !room || !picked ? [] : [{ id: 'door', label: 'Front door', icon: room.picks === 'bid' ? 'flag' : room.id === 'company' ? 'grid' : 'home', route: doorOf(room, project), order: -1 }];
  const wideOf = (items: NavItem[]): NavItem[] => ordered(items).filter(n => n.scope === 'room');
  const oneOf = (items: NavItem[]): NavItem[] => [...door(), ...(picked ? ordered(items).filter(n => n.scope !== 'room').map(n => ({ ...n, route: withId(n.route) })) : [])];
  // Arriving at a single room's door by link (Service, Warehouse, Company) is entering that room: the shell follows.
  // …and so is arriving at a room-wide page of Jobs or Bids (its schedule) from another room.
  useEffect(() => {
    // The most specific entry wins: /procurement/shelf is the Warehouse's Shelf, not Procurement on a job.
    const match = desktopNav.filter(n => !n.route.includes(':id') && (pathname === n.route || pathname.startsWith(n.route + '/'))).sort((a, b) => b.route.length - a.route.length)[0];
    const single = rooms.find(r => !r.picks && (pathname === r.door || pathname.startsWith(r.door + '/')));
    const byEntry = match ? rooms.find(r => r.id === match.room) : undefined;
    const hit = byEntry ?? single;
    // Opening a job's or a bid's front door by link (a row on the schedule, Today, a push) is picking that one.
    for (const r of rooms) { if (!r.picks) continue; const m = new RegExp('^' + r.door.replace(':id', '([^/]+)') + '(?:/|$)').exec(pathname); const id = m?.[1]; if (id && id !== project?.id && projects.some(p => p.id === id)) { enterProject(id); return; } }
    if (hit && room?.id !== hit.id) enterRoom(hit.id);
  }, [pathname, rooms, room, project, projects, enterRoom, enterProject, desktopNav]);
  const wide = room?.picks ? wideOf(desktopNav) : [];
  const one = room?.picks ? oneOf(desktopNav) : [...oneOf(desktopNav), ...wideOf(desktopNav)];
  const menu = [...wide, ...one];
  const phoneMenu = [...oneOf(phoneNav), ...wideOf(phoneNav)];
  const tabs = [{ id: 'home', label: 'Today', icon: 'home', route: '/', order: 0 }, ...(room ? [{ ...phoneMenu[0], label: room.picks ? room.label.replace(/s$/, '') : room.label }, ...phoneMenu.slice(1, 3)] : phoneNav.slice(0, 3)), MORE];
  const exact = (id: string) => id === 'door' || id === 'home';
  const ready = !!room && menu.length > 0;

  return (
    <div className="min-h-full md:grid md:grid-cols-[232px_1fr]">
      <aside className="hidden md:flex flex-col bg-surface border-r border-line sticky top-0 h-screen">
        <div className="flex items-center gap-2 px-4 pt-3 pb-2.5 shrink-0">
          <div className="w-[34px] h-[34px] rounded-[9px] bg-accent text-accent-ink grid place-items-center font-disp font-bold text-lg">T</div>
          <div className="min-w-0"><div className="lbl">TradeView</div><b className="font-head block truncate">{session?.displayName}</b></div>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-3 flex flex-col gap-0.5" style={{ scrollbarWidth: 'thin' }}>
          <SideLink to="/" icon="home" label="Today" exact />
          <div className="lbl px-2.5 pt-3 pb-1">You are in</div>
          <RoomBand variant="desk" />
          {ready && room!.picks && wide.length > 0 && <>
            <div className="lbl px-2.5 pt-3 pb-1">All {room!.label.toLowerCase()}</div>
            {wide.map(n => <SideLink key={n.id} to={n.route} icon={n.icon} label={n.label} exact />)}
          </>}
          {ready && one.length > 0 && <>
            <div className="lbl px-2.5 pt-3 pb-1">{room!.picks === 'job' ? 'On this job' : room!.picks === 'bid' ? 'In this bid' : `In ${room!.label}`}</div>
            {one.map(n => <SideLink key={n.id} to={n.route} icon={n.icon} label={n.label} exact={exact(n.id)} />)}
          </>}
          {ready && room!.picks && !picked && <>
            <div className="lbl px-2.5 pt-3 pb-1">{room!.picks === 'job' ? 'On a job' : 'In a bid'}</div>
            <p className="m-0 px-2.5 pb-1 text-[12px] text-muted">Open {room!.picks === 'job' ? 'a job' : 'a bid'} on the schedule and these open with it.</p>
            {[{ id: 'door', label: 'Front door', icon: room!.picks === 'bid' ? 'flag' : 'home' }, ...ordered(desktopNav).filter(n => n.scope !== 'room')].map(n => <span key={n.id} className="flex items-center gap-2.5 min-h-[34px] px-2.5 rounded-[9px] font-head font-semibold text-[13px] text-muted opacity-60" aria-disabled="true"><Icon name={n.icon} size={18} />{n.label}</span>)}
          </>}
          {!ready && <p className="m-0 px-2.5 pt-3 text-[12.5px] text-muted">Pick a room above and its things show here.</p>}
        </div>
        <div className="shrink-0 px-3 pt-2 pb-3 border-t border-line flex flex-col gap-0.5">
          <SideLink to="/modules" icon="grid" label="Modules" />
          <SideLink to="/settings" icon="settings" label="Settings" />
        </div>
      </aside>

      <div className="min-h-full flex flex-col">
        <header className="flex items-center justify-between gap-2.5 px-4 pt-3.5 pb-2 md:px-6">
          <div className="flex items-center gap-2 min-w-0 md:hidden">
            <div className="w-[34px] h-[34px] rounded-[9px] bg-accent text-accent-ink grid place-items-center font-disp font-bold text-lg shrink-0">T</div>
            <div className="min-w-0">
              <div className="lbl">TradeView</div>
              <b className="font-head block truncate text-[15px]">{session?.displayName}</b>
            </div>
          </div>
          <div className="hidden md:block lbl">{labelFor(pathname, [{ route: '/', label: 'Today' }, MORE, ...menu])}</div>
          <div className="flex items-center gap-1.5 shrink-0"><HelpButton /><NavLink to="/alerts" className="w-9 h-9 rounded-full bg-surface border border-line grid place-items-center relative shrink-0" aria-label={unread ? `${unread} unread alerts` : 'Alerts'}>
            <Icon name="bell" />
            {unread > 0 && <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-crit text-white text-[11px] font-head font-bold grid place-items-center leading-none">{unread > 99 ? '99+' : unread}</span>}
          </NavLink></div>
        </header>
        <div className="md:hidden"><RoomBand variant="phone" /></div>
        <Tour />

        {update && (
          <div className="mx-3.5 md:mx-6 mb-1 max-w-[1100px] w-auto lg:mx-auto flex items-center gap-2.5 bg-accent-soft text-accent rounded-[12px] px-3 py-2 text-[13.5px]" role="status">
            <Icon name="ai" size={18} className="shrink-0" /><span className="flex-1"><b className="font-head">A new version of TradeView is ready.</b></span>
            <button className="btn btn-sm btn-primary" onClick={applyUpdate}>Refresh</button>
          </div>
        )}
        <main className="flex-1 px-3.5 pb-[calc(9.5rem_+_env(safe-area-inset-bottom,0px))] md:px-6 md:pb-8 max-w-[1100px] w-full mx-auto">{children}</main>

        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-surface border-t border-line grid grid-cols-5 px-1.5 pt-2" style={{ paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))' }} aria-label="Primary">
          {tabs.filter(Boolean).map(t => (
            <NavLink key={t.id} to={t.route} end={t.route === '/' || exact(t.id)} className={({ isActive }) => `flex flex-col items-center gap-0.5 font-head font-semibold text-[10.5px] tracking-wide ${isActive ? 'text-accent' : 'text-muted'}`}>
              <Icon name={t.icon} size={22} />{t.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

function SideLink({ to, icon, label, exact }: { to: string; icon: string; label: string; exact?: boolean }) {
  return (
    <NavLink to={to} end={to === '/' || !!exact} className={({ isActive }) => `flex items-center gap-2.5 min-h-[34px] px-2.5 rounded-[9px] font-head font-semibold text-[13px] ${isActive ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-bg'}`}>
      <Icon name={icon} size={18} />{label}
    </NavLink>
  );
}

function labelFor(path: string, items: { route: string; label: string }[]) {
  const hit = items.filter(i => i.route === '/' ? path === '/' : path.startsWith(i.route)).sort((a, b) => b.route.length - a.route.length)[0];
  return hit?.label ?? '';
}
